import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Uuid } from './types';

interface CandidateRef {
  uuid: Uuid;
  full_name_latin?: string;
  full_name_local?: string;
}

export interface DashboardJobActive {
  uuid: Uuid;
  name: string;
  openings_count: number;
}

export interface DashboardTask {
  created_at: string;
  task_status: string;
  step: number | string;
  candidate: CandidateRef;
  job: { uuid: Uuid; name?: string };
}

export interface DashboardInterview {
  date: string;
  datetime: string;
  from_time: string;
  to_time: string;
  location: string;
  candidate: CandidateRef;
  job: Uuid | { uuid: Uuid };
  step: number | string;
}

export interface DashboardReminder {
  type: string;
  datetime: string;
  candidate: CandidateRef;
}

export interface DashboardOpening {
  uuid: Uuid;
  name: string;
  status: string;
  created: string;
  updated_at: string;
  team_lead: { uuid?: Uuid; full_name: string };
  job: { uuid: Uuid; name: string };
}

export interface DashboardNewJobHRL {
  uuid: Uuid;
  name: string;
}

export interface DashboardNewJobRM {
  uuid: Uuid;
  name: string;
  status: string;
  datetime: string;
  updated_at: string;
  team_lead: { full_name: string };
}

export interface DashboardState {
  listJobsActive: DashboardJobActive[];
  listTasks: DashboardTask[];
  listInterviews: DashboardInterview[];
  listReminders: DashboardReminder[];
  listOpenings: DashboardOpening[];
  listNewJobsHRL: DashboardNewJobHRL[];
  listNewJobsRM: DashboardNewJobRM[];
  listHrTasks: DashboardTask[];
}

export const initialState: DashboardState = {
  listJobsActive: [],
  listTasks: [],
  listInterviews: [],
  listReminders: [],
  listOpenings: [],
  listNewJobsHRL: [],
  listNewJobsRM: [],
  listHrTasks: [],
};

/* Discriminated union of all valid (field, data) pairs the action accepts. */
type SetListDataDashboardPayload = {
  [K in keyof DashboardState]: { field: K; data: DashboardState[K] };
}[keyof DashboardState];

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setListDataDashboard(state, { payload }: PayloadAction<SetListDataDashboardPayload>) {
      /* Cast resolves the per-field union; runtime check is enforced by callers. */
      (state[payload.field] as DashboardState[typeof payload.field]) = payload.data;
    },
    resetDashboardReducer(_state) {
      _state = initialState;
    },
  },
});

export const { setListDataDashboard, resetDashboardReducer } = dashboardSlice.actions;
export default dashboardSlice.reducer;
